import React, { useState } from 'react'

import {
  Box,
  Checkbox,
  Radio,
  TableBody,
  TableFooter,
  TableHead,
  TablePagination,
  TableSortLabel,
  useMediaQuery
} from '@mui/material'
import { visuallyHidden } from '@mui/utils'

import MobileCardList, { resolveMobileCards } from './MobileCardList'
import ScrollableTableContainer from './ScrollableTableContainer'
import SortableTableHead from './SortableTableHead'
import { stickyEndCellSx } from './stickyCell'
import {
  EnhancedTableProps,
  HeadCell,
  Order,
  TableParams
} from './Table.interface'
import {
  Table as EnhancedTable,
  TableCell as EnhancedTableCell,
  TableRow as EnhancedTableRow,
  TableHeadCell
} from './Table.styles'
import { getComparator, stableSort } from './Table.utils'

export const Table: React.FC<TableParams> = ({
  rows,
  header,
  page,
  handleChangePage,
  handleChangeRowsPerPage,
  rowsPerPage,
  rowsPerPageOptions = [25, 50, 100],
  totalCount = 0,
  hoverInRow = false,
  rowAction,
  hiddePagination = false,
  disableFilter,
  select,
  hideLastRowSeparator = false,
  sortableColumns,
  sortConfig,
  onMultiSort,
  multiSort = false,
  fontSize,
  density,
  stickyEndColumns = [],
  mobileCards
}) => {
  const { useSelect, selected, setSelected, singleSelect, persistAcrossPages } =
    select
  const isMobile = useMediaQuery('(max-width: 600px)', { noSsr: false })
  const stickyEndSet = new Set(stickyEndColumns)
  const [order, setOrder] = useState<Order>(undefined)
  const [orderBy, setOrderBy] = useState<string>('')

  const isSelected = (uuid: string) => selected && selected.indexOf(uuid) !== -1

  const headCellSx = (headCell: HeadCell) => {
    const base =
      headCell.width !== undefined ? { width: headCell.width } : undefined
    return stickyEndSet.has(headCell.id) ? stickyEndCellSx('head', base) : base
  }

  const pageUuids = (rows || []).map((row) => String(row.uuid))
  // Com seleção persistida entre páginas, `selected` carrega uuids que não estão
  // renderizados agora — o checkbox do cabeçalho precisa contar só os da página.
  const numSelected = persistAcrossPages
    ? pageUuids.filter((uuid) => isSelected(uuid)).length
    : (selected && selected.length) || 0

  function EnhancedTableHead(props: EnhancedTableProps) {
    const {
      order,
      orderBy,
      onRequestSort,
      numSelected,
      onSelectAllClick,
      rowCount
    } = props
    const createSortHandler =
      (property: string) => (event: React.MouseEvent<unknown>) => {
        onRequestSort(event, property)
      }

    return (
      <TableHead>
        <EnhancedTableRow>
          {useSelect && (
            <EnhancedTableCell padding="checkbox">
              {!singleSelect && (
                <Checkbox
                  color="primary"
                  indeterminate={numSelected > 0 && numSelected < rowCount}
                  checked={rowCount > 0 && numSelected === rowCount}
                  onChange={onSelectAllClick}
                />
              )}
            </EnhancedTableCell>
          )}
          {header.map((headCell) =>
            disableFilter ? (
              <TableHeadCell key={headCell.id} sx={headCellSx(headCell)}>
                {headCell.label}
              </TableHeadCell>
            ) : (
              <EnhancedTableCell
                key={headCell.id}
                sortDirection={orderBy === headCell.id ? order : false}
                sx={headCellSx(headCell)}
              >
                {!disableFilter && (
                  <TableSortLabel
                    active={orderBy === headCell.id}
                    direction={orderBy === headCell.id ? order : 'asc'}
                    onClick={createSortHandler(headCell.id)}
                  >
                    {headCell.label}
                    {orderBy === headCell.id ? (
                      <Box component="span" sx={visuallyHidden}>
                        {order === 'desc'
                          ? 'sorted descending'
                          : 'sorted ascending'}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                )}
              </EnhancedTableCell>
            )
          )}
        </EnhancedTableRow>
      </TableHead>
    )
  }

  const handleRequestSort = (
    _: React.MouseEvent<unknown>,
    property: string
  ) => {
    const isAsc = orderBy === property && order === 'asc'
    const isDesc = orderBy === property && order === 'desc'

    setOrderBy(isDesc ? '' : isAsc ? property : property)
    setOrder(isDesc ? undefined : isAsc ? 'desc' : 'asc')
  }

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = rows && rows.map((n) => n.uuid)
      !!setSelected &&
        setSelected((prevState: any) => ({
          ...prevState,
          selected: persistAcrossPages
            ? Array.from(
                new Set([...(prevState.selected || []), ...(newSelected || [])])
              )
            : newSelected
        }))
      return
    }
    !!setSelected &&
      setSelected((prevState: any) => ({
        ...prevState,
        // Desmarca apenas as linhas visíveis; o que veio de outras páginas fica.
        selected: persistAcrossPages
          ? (prevState.selected || []).filter(
              (uuid: string) => !pageUuids.includes(uuid)
            )
          : []
      }))
  }

  const handleClick = (uuid: string) => {
    if (selected) {
      // Se for singleSelect, seleciona apenas o item clicado
      if (singleSelect) {
        !!setSelected &&
          setSelected((prevState) => ({ ...prevState, selected: [uuid] }))
        return
      }

      const selectedIndex = selected.indexOf(uuid)
      let newSelected: string[] = []

      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selected, uuid)
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selected.slice(1))
      } else if (selectedIndex === selected.length - 1) {
        newSelected = newSelected.concat(selected.slice(0, -1))
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
          selected.slice(0, selectedIndex),
          selected.slice(selectedIndex + 1)
        )
      }

      !!setSelected &&
        setSelected((prevState) => ({ ...prevState, selected: newSelected }))
    }
  }
  // Use server-side sorting when sortableColumns is provided
  const useServerSideSorting = !!sortableColumns && sortableColumns.length > 0

  const mobileCardsConfig = resolveMobileCards(mobileCards, header)

  if (mobileCardsConfig && isMobile) {
    return (
      <MobileCardList
        rows={rows}
        config={mobileCardsConfig}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={totalCount}
        handleChangePage={handleChangePage}
        hiddePagination={hiddePagination}
        select={select}
        rowAction={rowAction}
        sortableColumns={sortableColumns}
        sortConfig={sortConfig}
        onMultiSort={onMultiSort}
      />
    )
  }

  return (
    <ScrollableTableContainer fontSize={fontSize} density={density}>
      <EnhancedTable>
        {useServerSideSorting ? (
          <SortableTableHead
            header={header}
            sortConfig={sortConfig}
            onMultiSort={onMultiSort}
            sortableColumns={sortableColumns}
            useSelect={useSelect}
            numSelected={numSelected}
            rowCount={(rows && rows.length) || 0}
            onSelectAllClick={handleSelectAllClick}
            multiSort={multiSort}
            singleSelect={singleSelect}
            stickyEndColumns={stickyEndColumns}
          />
        ) : (
          <EnhancedTableHead
            onRequestSort={handleRequestSort}
            order={order}
            orderBy={orderBy}
            numSelected={numSelected}
            onSelectAllClick={handleSelectAllClick}
            rowCount={(rows && rows.length) || 0}
          />
        )}
        <TableBody>
          {(useServerSideSorting
            ? rows || []
            : stableSort(rows || [], getComparator(order, orderBy))
          ).map(({ uuid, backgroundColor, clickable, ...row }, index) => {
            const isItemSelected = isSelected(uuid as string)
            const displayRows = useServerSideSorting
              ? rows || []
              : stableSort(rows || [], getComparator(order, orderBy))
            const isLastRow = index === displayRows.length - 1
            // Without an explicit flag, only rows that do something on click get the hand cursor
            const isRowClickable =
              typeof clickable === 'boolean'
                ? clickable
                : !!rowAction || !!useSelect
            // Hover highlight follows hoverInRow; an explicit clickable: false still turns it off
            const showRowHover = hoverInRow && clickable !== false

            return (
              <EnhancedTableRow
                sx={{
                  backgroundColor: backgroundColor ?? ''
                }}
                key={index}
                hoverInRow={showRowHover}
                isLastRow={isLastRow}
                hideLastRowSeparator={hideLastRowSeparator}
                {...(useSelect && {
                  onClick: () => handleClick(uuid as string)
                })}
              >
                {useSelect && (
                  <EnhancedTableCell component="th" scope="row">
                    {singleSelect ? (
                      <Radio color="primary" checked={isItemSelected} />
                    ) : (
                      <Checkbox color="primary" checked={isItemSelected} />
                    )}
                  </EnhancedTableCell>
                )}
                {Object.keys(row).map((key, i) => (
                  <EnhancedTableCell
                    component="th"
                    key={String(i)}
                    scope="row"
                    clickable={isRowClickable}
                    {...(stickyEndSet.has(key) && {
                      sx: stickyEndCellSx('body')
                    })}
                    {...(rowAction &&
                      isRowClickable && {
                        onClick: () => rowAction(String(uuid))
                      })}
                  >
                    {(() => {
                      const value = row[key]
                      return value &&
                        typeof value === 'object' &&
                        'component' in value
                        ? (value as any).component
                        : value
                    })()}
                  </EnhancedTableCell>
                ))}
              </EnhancedTableRow>
            )
          })}
        </TableBody>
        {!hiddePagination && (
          <TableFooter>
            <TablePagination
              count={totalCount}
              labelDisplayedRows={({ count, to, from }) =>
                `${from}–${to} ${
                  count !== -1 ? `de ${count}` : `mais que ${to}`
                }`
              }
              labelRowsPerPage="Registros por página:"
              onPageChange={(event, newPage) =>
                !!handleChangePage && handleChangePage(event, newPage)
              }
              onRowsPerPageChange={(event) =>
                !!handleChangeRowsPerPage && handleChangeRowsPerPage(event)
              }
              page={page ? page : 0}
              rowsPerPage={rowsPerPage ? rowsPerPage : 25}
              rowsPerPageOptions={rowsPerPageOptions}
            />
          </TableFooter>
        )}
      </EnhancedTable>
    </ScrollableTableContainer>
  )
}
